/**
 * "Somebody is timing this right now", for a register row.
 *
 * Read only: it starts and stops nothing, so it can be drawn beside a task
 * whoever is looking has no business controlling. It is the answer to the one
 * question a list of tasks cannot otherwise answer - two people have picked up
 * the same task this morning and neither can see the other doing it.
 *
 * Drawn for ANY running clock on the task, mine included, and the operator's own
 * is marked as such by tone rather than by a second component: on the register
 * the useful reading is "work is happening here", and whose it is comes second.
 *
 * Nothing at all when no clock is running, which is almost every row. A column
 * of empty marks would spend width on saying "no" over and over, which is the
 * same reason the hidden-from-client eye next to it has no column either.
 */
import { useT } from "../../i18n/I18nProvider";
import { useCrmUser } from "../shell/CrmUser";
import { ElapsedClock } from "./ElapsedClock";
import { useTimers } from "./TimerProvider";

export function TimerMark({ taskId }: { taskId: string }) {
  const t = useT();
  const user = useCrmUser();
  const timers = useTimers();
  const running = timers?.onTask(taskId) ?? [];
  // The operator's own if there is one, otherwise the oldest - the list arrives
  // sorted by start time, so "the oldest" is simply the first. Drawing one is
  // deliberate: a row is not the place to enumerate four people, and the count
  // beside the clock says how many there are.
  const own = running.find((r) => r.userId === user.id);
  const shown = own ?? running[0];
  if (!shown) return null;
  const label = own ? t("timer.runningMine") : t("timer.runningOther");

  return (
    <span
      className="crm-timer-mark relative z-[1] inline-flex shrink-0 items-center gap-1"
      title={label}
      aria-label={label}
      data-mine={own ? "1" : undefined}
    >
      <span className="crm-timer-dot" aria-hidden="true" />
      <ElapsedClock timer={shown} className="text-[11.5px] font-semibold" />
      {running.length > 1 ? <span className="text-[11.5px] font-semibold">+{running.length - 1}</span> : null}
    </span>
  );
}
